import {
  AlertState,
  TelemetryHistoryEnrichedDTO,
} from '@eppendorf/vnls-device-service-utils';
import { Switch, Button, DotsLoader } from '@eppendorf/vnls-react-components';
import { Thresholds } from '@eppendorf/vnls-react-components-preview';
import cn from 'classnames';
import { t } from 'i18next';
import { FormProvider } from 'react-hook-form';

import { AlertStatus } from '$features/monitoring/alert-status/alert-status';
import { parseBoolean } from '$features/monitoring/monitoring.utils';
import { getThresholdSetpointsFactorySnapshot } from '$features/monitoring/parameters/snapshot/thresholds-form-snapshot/create-threshold-configs-snapshot';
import { findTelemValueByType } from '$features/monitoring/parameters/snapshot/utils/find-telem-value-by-type';
import {
  createColorClassFor,
  getThresholdWarnText,
} from '$features/monitoring/parameters/utils';
import {
  createValueElement,
  applyFractionDigits,
} from '$features/monitoring/shared/formatting.utils';

import { MonitoringParameterFormErrorsSnapshot } from './monitoring-parameter-form-errors-snapshot';
import {
  ThresholdsFormOptionsSnapshot,
  useThresholdsFormSnapshot,
} from './use-thresholds-form-snapshot';

import './thresholds-form-snapshot.scss';

interface Props {
  isSensor: boolean;
  alertStatus?: AlertState;
  processValue?: TelemetryHistoryEnrichedDTO;
}

export function ThresholdsFormSnapshot({
  isSensor,
  alertStatus,
  shadowComponent,
  processValue,
  deviceId,
}: Props & ThresholdsFormOptionsSnapshot) {
  const {
    form,
    isEditing,
    canSubmit,
    isSubmitting,
    alertThresholdsEnabled,
    handleAlertThresholdsToggle,
    handleReset,
    handleEdit,
    handleSubmit,
  } = useThresholdsFormSnapshot({ shadowComponent, deviceId });

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading -- FormProvider is used to provide the form context
    <FormProvider {...form}>
      <div className="thresholds__edit-form">
        <div
          className={cn('thresholds__issue-card', {
            [createColorClassFor(alertStatus, 'border-color')]: alertStatus,
            'color-gray-900': !alertStatus,
          })}
        >
          <span className="thresholds__issue-card-label">
            <AlertStatus alertStatus={alertStatus} className="m-right-m" />
            <div>
              {getThresholdWarnText(
                !!parseBoolean(`${shadowComponent?.alertingEnabled}`),
                alertStatus,
                processValue?.sentAt,
              )}
            </div>
          </span>
          <div
            className={cn('thresholds__issue-card-value', {
              [createColorClassFor(alertStatus, 'color')]: alertStatus,
              'color-gray-900': !alertStatus,
            })}
          >
            {createValueElement(
              applyFractionDigits(processValue?.fractionalDigits),
              processValue?.valueType,
              findTelemValueByType(processValue),
            )}
            {shadowComponent?.unit}
          </div>
        </div>
        {!isSensor && (
          <div className="m-top-xl m-bottom-xl">{t('alertThresholds.alertHint')}</div>
        )}
        <form
          className="thresholds__widget"
          onSubmit={handleSubmit}
          onReset={handleReset}
        >
          {isSubmitting ? (
            <div className="flex flex__dir--column flex__ai--center gap-m flex__jc--center flex-grow">
              <DotsLoader />
              <p>{t('alertThresholds.loading')}</p>
              <p>{t('alertThresholds.loadingHint')}</p>
            </div>
          ) : (
            <fieldset className="thresholds__widget-fieldset">
              <div className="thresholds__widget-parameter-headline">
                <div className="thresholds__widget-parameter-label">
                  {t('alertThresholds.parameterAlert')}
                </div>
                <Switch
                  disabled={!isEditing}
                  checked={alertThresholdsEnabled}
                  onCheckedChange={handleAlertThresholdsToggle}
                />
              </div>
              <div className="thresholds__settings">
                <Thresholds
                  className="thresholds__negative-margin"
                  disabled={!isEditing && !alertThresholdsEnabled}
                  thresholds={getThresholdSetpointsFactorySnapshot(
                    shadowComponent,
                    isSensor,
                  )(isEditing)}
                />

                {isSensor && <MonitoringParameterFormErrorsSnapshot />}
                {isSensor && (
                  <div className="thresholds__form-actions">
                    {!isEditing && (
                      <Button size="small" onClick={handleEdit}>
                        {t('shared.edit')}
                      </Button>
                    )}

                    {isEditing && (
                      <div className="flex gap-m">
                        <Button size="small" variant="secondary" type="reset">
                          {t('shared.cancel')}
                        </Button>
                        <Button disabled={!canSubmit} size="small" type="submit">
                          {t('shared.save')}
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </fieldset>
          )}
        </form>
      </div>
    </FormProvider>
  );
}
